import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/configureStore";
import OT from "@opentok/client";
import SessionVideoPlaceholder from "./SessionVideoPlaceholder";
import SessionVideoStreams from "./SessionVideoStreams";
import PublisherControls from "./presenting/PublisherControls";
import PublisherSetup from "./presenting/PublisherSetup";
import { getSessionSetting } from "./selectors";
import { setSessionSetting } from "./sessionSlice";
import { VenueSessionState } from "../../app/types";
import { useJoinData, useSession } from "../api/helpers";
import LogError from "../logging/LogError";

interface Props {
  vonageSession: OT.Session | undefined;
}

const SessionVideo = ({ vonageSession }: Props) => {
  const session = useSession();
  const joinData = useJoinData();
  const dispatch = useAppDispatch();

  const showPublisherUI = useAppSelector(
    getSessionSetting(session?.sessionId, "showPublisherUI"),
  );
  const isFullScreen = useAppSelector(
    getSessionSetting(session?.sessionId, "isFullScreen"),
  );

  const [sessionState, setSessionState] =
    useState<VenueSessionState>("initial");

  useEffect(() => {
    setSessionState("initial");
  }, [session?.sessionId]);

  // Switch States
  useEffect(() => {
    const sessionStatus = session?.status ?? "NotStarted";

    if (vonageSession === undefined) {
      setSessionState("initial");
    } else if (
      (joinData?.role === "publisher" || joinData?.role === "moderator") &&
      sessionState === "initial"
    ) {
      setSessionState("publisherSetup");
    } else if (
      joinData?.role === "subscriber" &&
      sessionStatus === "NotStarted"
    ) {
      setSessionState("waitingToSubscribe");
    } else if (
      joinData?.role === "subscriber" &&
      sessionStatus === "InProgress"
    ) {
      setSessionState("subscribing");
    }
  }, [joinData?.role, session?.status, sessionState, vonageSession]);

  const onViewOnly = useCallback(() => {
    dispatch(setSessionSetting(session?.sessionId, "showPublisherUI", false));
    setSessionState("subscribing");
  }, [dispatch, session?.sessionId]);

  const onSetupComplete = useCallback(() => {
    dispatch(setSessionSetting(session?.sessionId, "showPublisherUI", false));
    setSessionState("prePublish");
  }, [dispatch, session?.sessionId]);

  const showControls = useMemo(
    () => ["prePublish", "publishing", "moderating"].includes(sessionState),
    [sessionState],
  );

  useEffect(() => {
    OT.on("exception", (event) => {
      LogError(event);
    });

    return () => OT.off("exception");
  }, []);

  return (
    <div className={`container ${isFullScreen && "full-screen"}`}>
      {vonageSession !== undefined ? (
        <SessionVideoStreams
          sessionState={sessionState}
          vonageSession={vonageSession}
        />
      ) : (
        <SessionVideoPlaceholder />
      )}
      {showControls && <PublisherControls />}
      {(sessionState === "publisherSetup" || showPublisherUI) && (
        <PublisherSetup
          onViewOnly={onViewOnly}
          onSetupComplete={onSetupComplete}
          vonageSession={vonageSession}
        />
      )}
      <style jsx>
        {`
          .container {
            position: relative;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            background: black;
            height: 60vh;
            width: 100%;
            flex-direction: column;
          }
          .full-screen {
            position: fixed;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
            background: rgba(51, 51, 51);
            z-index: 10;
          }
          @media (max-width: 700px) {
            .container {
              height: 68vh;
            }
          }
        `}
      </style>
    </div>
  );
};

export default SessionVideo;
